// src/types/payments.ts
/**
 * CONSOLIDATED PAYMENT TYPES
 * Tập trung tất cả type definitions liên quan đến payment
 */

import { BaseModel, ID, StrapiRelationOne } from './common';
import { User } from './users';
import { License } from './licenses';
import { Invoice } from './documents';

// ============================================================================
// ENUMS & CONSTANTS
// ============================================================================

export enum PaymentStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  REFUNDED = 'refunded',
  PARTIALLY_REFUNDED = 'partially_refunded',
  CANCELLED = 'cancelled'
}

export enum PaymentMethod {
  MOMO = 'momo',
  VNPAY = 'vnpay',
  ZALOPAY = 'zalopay',
  BANK_TRANSFER = 'bank_transfer',
  CREDIT_CARD = 'credit_card',
  DEBIT_CARD = 'debit_card',
  EWALLET = 'ewallet',
  CASH = 'cash',
  CRYPTO = 'crypto'
}

export enum PaymentGateway {
  MOMO = 'momo',
  VNPAY = 'vnpay',
  ZALOPAY = 'zalopay',
  STRIPE = 'stripe',
  PAYPAL = 'paypal',
  VIETCOMBANK = 'vietcombank',
  TECHCOMBANK = 'techcombank',
  BIDV = 'bidv',
  INTERNAL = 'internal'
}

export enum RefundReason {
  CUSTOMER_REQUEST = 'customer_request',
  FRAUD = 'fraud',
  DUPLICATE = 'duplicate',
  PROCESSING_ERROR = 'processing_error',
  MERCHANT_REQUEST = 'merchant_request',
  CHARGEBACK = 'chargeback',
  OTHER = 'other'
}

export enum RefundStatus {
  NONE = 'none',
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled'
}

export enum FraudCheckStatus {
  NOT_CHECKED = 'not_checked',
  PASSED = 'passed',
  FLAGGED = 'flagged',
  FAILED = 'failed'
}

// ============================================================================
// BASE PAYMENT MODEL (from Strapi Schema)
// ============================================================================

export interface Payment extends BaseModel {
  // Identifiers
  paymentId: string; // Unique
  
  // Amount & Currency
  amount: number;
  currency: string;
  netAmount?: number;
  taxAmount?: number;
  taxRate?: number;
  discountAmount?: number;
  
  // Status
  paymentStatus: PaymentStatus;
  paymentMethod: PaymentMethod;
  paymentGateway: PaymentGateway;
  
  // Gateway Info
  gatewayTransactionId?: string;
  gatewayReference?: string;
  gatewayStatus?: string;
  gatewayResponse?: Record<string, any>;
  gatewayFee?: number;
  gatewayFeePercentage?: number;
  
  // URLs
  paymentUrl?: string;
  redirectUrl?: string;
  callbackUrl?: string;
  
  // Dates
  dueDate: string;
  transactionDate?: string;
  confirmedAt?: string;
  expiresAt?: string;
  
  // Refund
  refundAmount?: number;
  refundReason?: RefundReason;
  refundReasonDetails?: string;
  refundStatus: RefundStatus;
  refundRequestedAt?: string;
  refundProcessedAt?: string;
  refundGatewayTransactionId?: string;
  refundGatewayResponse?: Record<string, any>;
  
  // Description & Notes
  description?: string;
  notes?: string;
  internalNotes?: string;
  metadata?: Record<string, any>;
  
  // Security & Tracking
  ipAddress?: string;
  userAgent?: string;
  riskScore?: number;
  fraudCheck: FraudCheckStatus;
  
  // Customer Info
  customerEmail?: string;
  customerPhone?: string;
  customerName?: string;
  billingAddress?: Record<string, any>;
  
  // Retry Logic
  retryCount?: number;
  lastRetryAt?: string;
  nextRetryAt?: string;
  
  // Notifications
  webhookDelivered: boolean;
  webhookDeliveredAt?: string;
  notificationSent: boolean;
  
  // Items
  paymentItems?: Record<string, any>[];
  
  // Installment
  isInstallment: boolean;
  installmentPlan?: {
    totalInstallments: number;
    installmentAmount: number;
    schedule: Array<{
      number: number;
      amount: number;
      dueDate: string;
      status: PaymentStatus;
    }>;
  };
  installmentNumber?: number;
  totalInstallments?: number;
  
  // Commission & Split
  commission_rate?: number;
  commission_amount?: number;
  seller_amount?: number;
  platform_fee?: number;
  payment_split?: {
    platform: number;
    seller: number;
    tax?: number;
  };
  
  // Relations
  payer?: StrapiRelationOne<User>;
  receiver?: StrapiRelationOne<User>;
  refundRequestedBy?: StrapiRelationOne<User>;
  refundProcessedBy?: StrapiRelationOne<User>;
  invoice?: StrapiRelationOne<Invoice>;
  license?: StrapiRelationOne<License>;
  parentPayment?: StrapiRelationOne<Payment>;
  childPayments?: Payment[];
}

// ============================================================================
// SERVICE INTERFACES
// ============================================================================

export interface CreatePaymentData {
  // Required
  licenseRequestId?: string;
  amount: number;
  paymentMethod: PaymentMethod;
  
  // Optional
  currency?: string;
  description?: string;
  customerEmail?: string;
  customerPhone?: string;
  customerName?: string;
  redirectUrl?: string;
  notifyUrl?: string;
  metadata?: Record<string, any>;
  
  // Installment
  isInstallment?: boolean;
  installmentPlan?: Payment['installmentPlan'];
}

export interface PaymentGatewayResponse {
  paymentId: string;
  gatewayTransactionId: string;
  paymentUrl?: string;
  qrCode?: string;
  redirectUrl?: string;
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  message: string;
  expiresAt?: string;
  gatewayData?: Record<string, any>;
}

export interface PaymentFilters {
  status?: PaymentStatus[];
  method?: PaymentMethod[];
  dateFrom?: string;
  dateTo?: string;
  amountMin?: number;
  amountMax?: number;
  userId?: string;
  licenseId?: string;
}

export interface PaymentStats {
  totalRevenue: number;
  thisMonthRevenue: number;
  lastMonthRevenue: number;
  pendingPayments: number;
  completedPayments: number;
  platformCommission: number;
  ownerRevenue: number;
  averageTransactionValue: number;
  totalTransactions: number;
  successRate: number;
  refundRate: number;
  topPaymentMethods: Array<{
    method: string;
    count: number;
    amount: number;
  }>;
}

export interface RevenueBreakdown {
  totalRevenue: number;
  platformCommission: number;
  ownerRevenue: number;
  taxAmount: number;
  netRevenue: number;
  payments: Payment[];
  period: {
    from: string;
    to: string;
  };
  commissionRate: number;
}

export interface FraudCheckResult {
  riskScore: number;
  riskLevel: 'low' | 'medium' | 'high';
  flags: string[];
  recommendation: 'approve' | 'review' | 'reject';
}

export interface InstallmentPaymentData {
  baseAmount: number;
  numberOfInstallments: number;
  frequency: 'weekly' | 'biweekly' | 'monthly';
  startDate: string;
  customerData: {
    email: string;
    phone: string;
    name: string;
  };
}

// ============================================================================
// PAYMENT TRANSACTION (for Store)
// ============================================================================

export interface PaymentTransaction {
  id: string;
  licenseRequestId: string;
  amount: number;
  method: PaymentMethod;
  status: PaymentStatus;
  createdAt: string;
  completedAt?: string;
  failureReason?: string;
  retryCount: number;
  maxRetries: number;
}

// ============================================================================
// WEBHOOK & GATEWAY SPECIFIC
// ============================================================================

export interface MoMoWebhook {
  partnerCode: string;
  orderId: string;
  requestId: string;
  amount: number;
  orderInfo: string;
  orderType: string;
  transId: string;
  resultCode: number;
  message: string;
  payType: string;
  responseTime: number;
  extraData: string;
  signature: string;
}

export interface VNPayWebhook {
  vnp_TxnRef: string;
  vnp_Amount: string;
  vnp_OrderInfo: string;
  vnp_ResponseCode: string;
  vnp_TransactionNo: string;
  vnp_BankCode: string;
  vnp_PayDate: string;
  vnp_SecureHash: string;
}

export interface ZaloPayWebhook {
  app_id: number;
  app_trans_id: string;
  app_time: number;
  app_user: string;
  amount: number;
  embed_data: string;
  item: string;
  zp_trans_id: string;
  server_time: number;
  channel: number;
  merchant_user_id: string;
  user_fee_amount: number;
  discount_amount: number;
  return_code: number;
  return_message: string;
  sub_return_code: number;
  sub_return_message: string;
  mac: string;
}

// ============================================================================
// EXPORT ALL
// ============================================================================

// export type {
//   Payment,
//   CreatePaymentData,
//   PaymentGatewayResponse,
//   PaymentFilters,
//   PaymentStats,
//   RevenueBreakdown,
//   FraudCheckResult,
//   InstallmentPaymentData,
//   PaymentTransaction,
//   MoMoWebhook,
//   VNPayWebhook,
//   ZaloPayWebhook
// };

// export {
//   PaymentStatus,
//   PaymentMethod,
//   PaymentGateway,
//   RefundReason,
//   RefundStatus,
//   FraudCheckStatus
// };
